import { useMemo, useState } from "react";
import { SortDirection } from "../../../models/person.model";
import { EmployeeListFilter } from "../models/employeeOverview.model";

export function useEmployeeOverviewFilters() {
    const [searchQuery, setSearchQuery] = useState("");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState(SortDirection.Ascending);

    const [costCenterIds, setCostCenterIds] = useState<number[]>([]);
    const [projectIds, setProjectIds] = useState<number[]>([]);
    const [workScheduleIds, setWorkScheduleIds] = useState<number[]>([]);
    const [contractTypeIds, setContractTypeIds] = useState<number[]>([]);
    const [positionIds, setPositionIds] = useState<number[]>([]);
    const [includeNoPosition, setIncludeNoPosition] = useState(false);

    const [workStartFrom, setWorkStartFrom] = useState<string | null>(null);
    const [workStartTo, setWorkStartTo] = useState<string | null>(null);
    const [workEndFrom, setWorkEndFrom] = useState<string | null>(null);
    const [workEndTo, setWorkEndTo] = useState<string | null>(null);
    const [availabilityFrom, setAvailabilityFrom] = useState<string | null>(null);
    const [availabilityTo, setAvailabilityTo] = useState<string | null>(null);
    const [noPersonId, setNoPersonId] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(false);

    const handleSortChange = (field: string) => {
        if (field === "none") {
            setSortField(null);
            setSortDirection(SortDirection.Ascending);
        } else if (sortField === field) {
            setSortDirection(
                sortDirection === SortDirection.Ascending
                    ? SortDirection.Descending
                    : SortDirection.Ascending
            );
        } else {
            setSortField(field);
            setSortDirection(SortDirection.Ascending);
        }
        setPageIndex(0);
    };

    const handlePageSizeChange = (value: string) => {
        setPageSize(parseInt(value, 10));
        setPageIndex(0);
    };

    const handlePageChange = (newPage: number) => {
        setPageIndex(newPage - 1);
    };

    const filter: EmployeeListFilter = useMemo(
        () => ({
            pageIndex,
            pageSize,
            keywords: searchQuery.trim() ? searchQuery.trim().split(/\s+/) : undefined,
            sortField: sortField ?? undefined,
            sortDirection,
            costCenterIds: costCenterIds.length ? costCenterIds : undefined,
            projectIds: projectIds.length ? projectIds : undefined,
            workScheduleIds: workScheduleIds.length ? workScheduleIds : undefined,
            contractTypeIds: contractTypeIds.length ? contractTypeIds : undefined,
            positionIds: positionIds.length ? positionIds : undefined,
            includeNoPosition,
            workStartFrom: workStartFrom ?? undefined,
            workStartTo: workStartTo ?? undefined,
            workEndFrom: workEndFrom ?? undefined,
            workEndTo: workEndTo ?? undefined,
            availabilityFrom: availabilityFrom ?? undefined,
            availabilityTo: availabilityTo ?? undefined,
            noPersonId,
            includeInactive,
        }),
        [
            pageIndex,
            pageSize,
            searchQuery,
            sortField,
            sortDirection,
            costCenterIds,
            projectIds,
            workScheduleIds,
            contractTypeIds,
            positionIds,
            includeNoPosition,
            workStartFrom,
            workStartTo,
            workEndFrom,
            workEndTo,
            availabilityFrom,
            availabilityTo,
            noPersonId,
            includeInactive,
        ]
    );

    return {
        searchQuery,
        setSearchQuery,
        pageIndex,
        setPageIndex,
        pageSize,
        setPageSize,
        sortField,
        sortDirection,
        costCenterIds,
        setCostCenterIds,
        projectIds,
        setProjectIds,
        workScheduleIds,
        setWorkScheduleIds,
        contractTypeIds,
        setContractTypeIds,
        positionIds,
        setPositionIds,
        includeNoPosition,
        setIncludeNoPosition,
        workStartFrom,
        setWorkStartFrom,
        workStartTo,
        setWorkStartTo,
        workEndFrom,
        setWorkEndFrom,
        workEndTo,
        setWorkEndTo,
        availabilityFrom,
        setAvailabilityFrom,
        availabilityTo,
        setAvailabilityTo,
        noPersonId,
        setNoPersonId,
        includeInactive,
        setIncludeInactive,
        handleSortChange,
        handlePageSizeChange,
        handlePageChange,
        filter,
    };
}
